import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import moment from 'moment';
import { useCartContext } from '../context/cartContext';
import { Heading } from '../component/Heading';
import { AiOutlineWarning, AiOutlineDelete, AiOutlineRight } from 'react-icons/ai';
import { MdError } from 'react-icons/md';
import { ROOT_URL } from '../config';
import { Button } from 'react-bootstrap';
import server from '../server';

type CartItemData = {
  id: string | number;
  attributes: {
    setmenu?: { data?: { id?: number; attributes?: any } };
    shop?: { data?: { attributes?: any } };
    booking_date?: string;
    booking_time?: string;
    quantity?: number;
    dynamic_price?: number;
  };
};

const CartItemComponent = ({ item }: { item: CartItemData }) => {
  console.log({ cartItem: item })
  const { removeFromCart, updateQuantity, cartList } = useCartContext();

  const itemDetail = item.attributes ?? {};
  const menuDetail = itemDetail.setmenu?.data?.attributes ?? {};
  const shop = itemDetail.shop?.data?.attributes ?? {};

  const isExpired = useMemo(() => {
    const bookingDate = itemDetail.booking_date;
    const bookingTime = itemDetail.booking_time;
    if (!bookingDate) return false;
    if (bookingTime) {
      const bookingMoment = moment(`${bookingDate} ${bookingTime}`);
      return bookingMoment.isBefore(moment());
    }
    const today = moment().startOf('day');
    return moment(bookingDate).isBefore(today);
  }, [itemDetail.booking_date, itemDetail.booking_time]);

  const cartHistory = useMemo(() => {
    return cartList.reduce((acc, cur) => {
      const currentSetMenuId = cur.attributes.setmenu?.data?.id;
      const currentQuantity = Number(cur.attributes.quantity);
      if (currentSetMenuId !== undefined && currentSetMenuId !== null) {
        acc[currentSetMenuId] = (acc[currentSetMenuId] ?? 0) + currentQuantity;
      }
      return acc;
    }, {} as Record<string | number, number>);
  }, [cartList]);

  const handleMinus = async () => {
    try {
      const newQuantity = Math.max(1, Number(itemDetail.quantity ?? 1) - 1);
      await updateQuantity(item.id, newQuantity);
    } catch (error) {
      console.error('Failed to update quantity:', error);
    }
  };

  const handleAdd = async () => {
    try {
      const currentQty = Number(itemDetail.quantity ?? 1);
      const setMenuId = itemDetail.setmenu?.data?.id;

      if (!setMenuId) {
        alert('無法取得套餐信息');
        return;
      }
      const userSetMenu = await server.findAll("user-campaign-setmenus", {
        filters: {
          // user: { id: { $null: true } },
          email: { $null: true },
          phone: { $null: true },
          setmenu: setMenuId,
        },
      });
      const countingSetMenu = userSetMenu?.length || 0;
      const maxCheckout = menuDetail.user_max_checkout_quantity ?? Infinity;
      const maxTotal = menuDetail.user_max_quantity ?? Infinity;
      const currentTotal = cartHistory[setMenuId] ?? 0;



      if (currentQty + 1 > maxCheckout) {
        alert(`每位顧客每次只能購買${maxCheckout}份套餐`);
        return;
      }

      if (currentTotal + 1 > maxTotal) {
        alert(`每位顧客最多只能購買${maxTotal}份套餐`);
        return;
      }
      if (currentQty + 1 > countingSetMenu) {
        alert('該套餐庫存只剩餘' + countingSetMenu + '份');
        return;
      }
      updateQuantity(item.id, currentQty + 1);
    } catch (error) {
      console.error('Failed to update quantity:', error);
    }
  };

  const handleRemove = async () => {
    try {
      removeFromCart(item.id);
    } catch (error) {
      console.error('Failed to remove item:', error);
    }
  };

  const displayPrice = itemDetail.dynamic_price ?? menuDetail.price ?? 0;

  return (
    <div
      style={{
        padding: 24,
        backgroundColor: isExpired ? '#FFE5E5' : 'white',
        border: isExpired ? '2px solid #FF0000' : 'none',
        borderRadius: 12,
        marginLeft: 8,
        marginRight: 8,
        marginTop: 20,
        // marginBottom: 4,
      }}
    >
      {isExpired && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: '#FF0000',
            padding: 8,
            borderRadius: 6,
            marginBottom: 12,
            color: 'white',
            fontSize: 14,
            fontWeight: 'bold',
            gap: 6,
          }}
        >
          <AiOutlineWarning size={20} />
          <span>預約日期已過期，請移除此項目</span>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'row', gap: 16, marginBottom: 16 }}>
        <div
          style={{
            width: 86,
            height: 80,
            overflow: 'hidden',
            borderRadius: 10,
            flexShrink: 0,
          }}
        >
          <img
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            src={`${ROOT_URL}${menuDetail?.photo_title?.data?.attributes?.formats?.small?.url || menuDetail?.photo_title?.data?.attributes?.formats?.thumbnail?.url}`}
            alt={menuDetail?.title}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
          <div style={{ fontWeight: 400, fontSize: 18 }}>{menuDetail?.title}</div>
          <div style={{ fontWeight: 400, fontSize: 12, color: '#7A7E80' }}>
            {menuDetail?.description}
          </div>
          <div style={{ fontWeight: 400, fontSize: 18, color: "var(--bs-primary)" }}>
            {`$ ${displayPrice}`}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <span style={{ color: '#7A7E80', marginRight: 8 }}>店鋪:</span>
          <span>{shop?.name}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <span style={{ color: '#7A7E80', marginRight: 8 }}>時間:</span>
          <span style={{ color: isExpired ? '#FF0000' : '#000', fontWeight: isExpired ? 'bold' : 'normal' }}>
            {`${itemDetail.booking_date?.replace(/-/g, '/')} ${itemDetail.booking_time?.slice(0, 5)}`}
          </span>
          {isExpired && <MdError color="#FF0000" size={18} style={{ marginLeft: 6 }} />}
        </div>
      </div>

      <div
        style={{
          // marginVertical: 8,
          marginBlock: 8,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Button
            type="button"
            onClick={handleMinus}
            disabled={isExpired || Number(itemDetail.quantity) <= 1}
            style={{
              width: 36,
              height: 36,
              border: `1px solid ${"var(--bs-primary)"}`,
              backgroundColor: 'white',
              color: "var(--bs-primary)",
              borderRadius: 4,
              fontSize: 20,
              cursor: isExpired || Number(itemDetail.quantity) <= 1 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isExpired || Number(itemDetail.quantity) <= 1 ? 0.5 : 1,
            }}
          >
            −
          </Button>
          <span style={{ fontSize: 18, fontWeight: '700', minWidth: 24, textAlign: 'center' }}>
            {itemDetail.quantity}
          </span>
          <Button
            type="button"
            onClick={handleAdd}
            disabled={isExpired}
            style={{
              width: 36,
              height: 36,
              border: `1px solid ${"var(--bs-primary)"}`,
              backgroundColor: 'white',
              color: "var(--bs-primary)",
              borderRadius: 4,
              fontSize: 20,
              cursor: isExpired ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isExpired ? 0.5 : 1,
            }}
          >
            +
          </Button>
        </div>
        <button
          type="button"
          onClick={handleRemove}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: "var(--bs-primary)",
            padding: 0,
          }}
        >
          <AiOutlineDelete size={24} />
        </button>
      </div>
    </div>
  );
};

const CONTENT_MAX_WIDTH = 860;

export default function Cart() {
  const navigate = useNavigate();
  const { company: companyParam } = useParams();
  const { cartList, cartLoading, totalPrice, counting } = useCartContext();

  const hasExpiredItems = useMemo(() => {
    if (!cartList || cartList.length === 0) return false;
    const now = moment();
    const today = moment().startOf('day');
    return cartList.some((item) => {
      const bookingDate = item.attributes.booking_date;
      const bookingTime = item.attributes.booking_time;
      if (!bookingDate) return false;
      if (bookingTime) {
        return moment(`${bookingDate} ${bookingTime}`).isBefore(now);
      }
      return moment(bookingDate).isBefore(today);
    });
  }, [cartList]);

  const handleCheckout = () => {
    if (hasExpiredItems) {
      alert('購物車中有已過期的預約項目，請先移除這些項目後再繼續結帳。');
      return;
    }
    console.log({ cartList })
    navigate(`/${companyParam}/checkout`);
  };

  return (
    <div style={{ minHeight: '100vh' }}>

      <div
        style={{
          flex: 1,
          width: '100%',
          maxWidth: CONTENT_MAX_WIDTH,
          margin: '0 auto',
          padding: 16,
          paddingBottom: 200,
          boxSizing: 'border-box',
        }}
      >
        {cartLoading && <div style={{ textAlign: 'center', padding: '20px' }}>加載中...</div>}

        {!cartLoading && cartList.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <p>購物車是空的</p>
          </div>
        )}

        {cartList.map((item) => (
          <CartItemComponent key={item.id} item={item} />
        ))}
      </div>

      {/* Footer */}
      <div
        className='bg-bg'
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          borderTop: '1px solid #CDCFD0',
          padding: 16,
        }}
      >
        <div style={{ width: '100%', maxWidth: CONTENT_MAX_WIDTH, margin: '0 auto' }}>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontWeight: '700', fontSize: 16 }}>總計金額</div>
            <div style={{ color: "var(--bs-primary)", fontSize: 18, fontWeight: '700' }}>
              {`$ ${totalPrice}`}
            </div>
          </div>

          {hasExpiredItems && (
            <div
              style={{
                backgroundColor: '#FFF3CD',
                padding: 12,
                borderRadius: 8,
                marginBottom: 8,
                color: '#856404',
                fontSize: 14,
                textAlign: 'center',
              }}
            >
              ⚠️ 購物車中有已過期的預約項目，請移除後再結帳
            </div>
          )}

          <Button
            type="button"
            onClick={handleCheckout}
            disabled={counting === 0 || hasExpiredItems}
            style={{
              width: '100%',
              backgroundColor: counting === 0 || hasExpiredItems ? 'gray' : "var(--bs-primary)",
              paddingTop: 16,
              paddingBottom: 16,
              alignItems: 'center',
              justifyContent: 'space-around',
              borderRadius: 50,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'row',
              border: 'none',
              cursor: counting === 0 || hasExpiredItems ? 'not-allowed' : 'pointer',
              color: 'white',
              fontSize: 16,
              fontWeight: 500,
            }}
          >
            <div style={{ width: 24 }} />
            <span>下一步</span>
            <AiOutlineRight size={24} />
          </Button>
        </div>
      </div>
    </div>
  );
}